/**
 * Proactive Engagement Hook
 *
 * Evaluates when to show proactive AI messages based on
 * user behavior and context.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCustomerIntelligence } from '../contexts/CustomerIntelligenceContext';

// Time duration constants (in seconds unless noted)
const SHORT_SESSION_THRESHOLD_SECONDS = 60;
const LONG_PRODUCT_VIEW_THRESHOLD_SECONDS = 90;
const CART_DWELL_THRESHOLD_SECONDS = 120;
const CHECKOUT_DWELL_THRESHOLD_SECONDS = 180;
const FIRST_TIME_MIN_SESSION_SECONDS = 45;
const FIRST_TIME_MAX_SESSION_SECONDS = 120;
const CATEGORY_PAGES_THRESHOLD = 4;

// Cooldown constants (in minutes)
const COOLDOWN_DAILY_MINUTES = 1440;
const COOLDOWN_LONG_MINUTES = 60;
const COOLDOWN_MEDIUM_MINUTES = 30;
const COOLDOWN_SHORT_MINUTES = 15;
const COOLDOWN_VERY_SHORT_MINUTES = 10;

// Timer constants (in milliseconds)
const INITIAL_EVALUATION_DELAY_MS = 5000;
const PERIODIC_EVALUATION_INTERVAL_MS = 30000;
const PAGE_CHANGE_EVALUATION_DELAY_MS = 3000;
const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const EXTENDED_COOLDOWN_MULTIPLIER = 2;

// Limits
const MAX_PROACTIVE_PER_SESSION = 2;
const MAX_DAILY_DISMISSALS = 3;
const DEFAULT_SESSION_COUNT = 1;
const DEFAULT_RADIX = 10;

// Priority levels (higher = more urgent)
const PRIORITY_RETURNING_CUSTOMER = 9;
const PRIORITY_CHECKOUT_HELP = 7;
const PRIORITY_LONG_PRODUCT_VIEW = 6;
const PRIORITY_BROWSE_ASSIST = 5;
const PRIORITY_FIRST_TIME = 3;

// Max dismissals before permanently disabling
const MAX_DISMISSALS_RETURNING = 3;
const MAX_DISMISSALS_STANDARD = 2;
const MAX_DISMISSALS_FIRST_TIME = 1;

interface ProactiveTrigger {
  id: string;
  condition: (ctx: CustomerContext) => boolean;
  getMessage: (ctx: CustomerContext) => string;
  cooldownMinutes: number;
  priority: number;
  maxDismissalsBeforeDisable: number;
}

interface CustomerContext {
  fingerprintHash: string;
  customerId?: string;
  isRecognized: boolean;
  sessionDuration: number;
  timeOnCurrentPage: number;
  currentPageType: 'home' | 'product' | 'category' | 'cart' | 'checkout' | 'search' | 'general';
  currentProductId?: string;
  currentProductName?: string;
  pagesVisitedThisSession: string[];
  searchQueries: string[];
  sessionCount: number;
  daysSinceLastPurchase?: number;
  hasAbandonedCart: boolean;
  abandonedCartAge?: number;
  petNames?: string[];
  chatMessagesThisSession: number;
  proactiveMessagesDismissedToday: number;
}

interface ProactiveEngagementResult {
  shouldShow: boolean;
  message: string | null;
  triggerId: string | null;
}

// Storage keys
const COOLDOWN_KEY = 'petmas_proactive_cooldowns';
const DISMISSAL_KEY = 'petmas_proactive_dismissals';
const SESSION_COUNT_KEY = 'petmas_session_proactive_count';

// Proactive triggers
const TRIGGERS: ProactiveTrigger[] = [
  {
    id: 'returning-customer-greeting',
    condition: (ctx) =>
      ctx.isRecognized &&
      ctx.sessionCount > 1 &&
      ctx.chatMessagesThisSession === 0 &&
      ctx.sessionDuration < SHORT_SESSION_THRESHOLD_SECONDS,
    getMessage: (ctx) => {
      if (ctx.petNames && ctx.petNames.length > 0) {
        return `¡Bienvenido de nuevo! ¿Como esta ${ctx.petNames[0]}?`;
      }
      return '¡Bienvenido de nuevo! ¿En que puedo ayudarte?';
    },
    cooldownMinutes: COOLDOWN_DAILY_MINUTES,
    priority: PRIORITY_RETURNING_CUSTOMER,
    maxDismissalsBeforeDisable: MAX_DISMISSALS_RETURNING,
  },
  {
    id: 'long-product-view',
    condition: (ctx) =>
      ctx.currentPageType === 'product' &&
      ctx.timeOnCurrentPage > LONG_PRODUCT_VIEW_THRESHOLD_SECONDS &&
      ctx.chatMessagesThisSession === 0,
    getMessage: (ctx) =>
      ctx.currentProductName
        ? `¿Tienes preguntas sobre ${ctx.currentProductName}?`
        : '¿Tienes preguntas sobre este producto?',
    cooldownMinutes: COOLDOWN_VERY_SHORT_MINUTES,
    priority: PRIORITY_LONG_PRODUCT_VIEW,
    maxDismissalsBeforeDisable: MAX_DISMISSALS_STANDARD,
  },
  {
    id: 'browsing-category-help',
    condition: (ctx) => {
      const categoryPages = ctx.pagesVisitedThisSession.filter(p =>
        p.includes('/perros') || p.includes('/gatos') ||
        p.includes('/farmacia')
      );
      return categoryPages.length >= CATEGORY_PAGES_THRESHOLD && ctx.chatMessagesThisSession === 0;
    },
    getMessage: () => '¿Necesitas ayuda para encontrar algo especifico?',
    cooldownMinutes: COOLDOWN_SHORT_MINUTES,
    priority: PRIORITY_BROWSE_ASSIST,
    maxDismissalsBeforeDisable: MAX_DISMISSALS_STANDARD,
  },
  {
    id: 'cart-assistance',
    condition: (ctx) =>
      ctx.currentPageType === 'cart' &&
      ctx.timeOnCurrentPage > CART_DWELL_THRESHOLD_SECONDS &&
      ctx.chatMessagesThisSession === 0,
    getMessage: () => '¿Tienes todo lo que necesitas para tu mascota?',
    cooldownMinutes: COOLDOWN_MEDIUM_MINUTES,
    priority: PRIORITY_BROWSE_ASSIST,
    maxDismissalsBeforeDisable: MAX_DISMISSALS_STANDARD,
  },
  {
    id: 'checkout-assistance',
    condition: (ctx) =>
      ctx.currentPageType === 'checkout' &&
      ctx.timeOnCurrentPage > CHECKOUT_DWELL_THRESHOLD_SECONDS &&
      ctx.chatMessagesThisSession === 0,
    getMessage: () => '¿Necesitas ayuda con tu compra?',
    cooldownMinutes: COOLDOWN_LONG_MINUTES,
    priority: PRIORITY_CHECKOUT_HELP,
    maxDismissalsBeforeDisable: MAX_DISMISSALS_FIRST_TIME,
  },
  {
    id: 'first-time-welcome',
    condition: (ctx) =>
      !ctx.isRecognized &&
      ctx.sessionDuration > FIRST_TIME_MIN_SESSION_SECONDS &&
      ctx.sessionDuration < FIRST_TIME_MAX_SESSION_SECONDS &&
      ctx.chatMessagesThisSession === 0,
    getMessage: () => '¡Hola! Soy el asistente de PetMas. ¿Puedo ayudarte?',
    cooldownMinutes: COOLDOWN_DAILY_MINUTES,
    priority: PRIORITY_FIRST_TIME,
    maxDismissalsBeforeDisable: MAX_DISMISSALS_FIRST_TIME,
  },
];

export function useProactiveEngagement(chatMessagesCount: number) {
  const [result, setResult] = useState<ProactiveEngagementResult>({
    shouldShow: false,
    message: null,
    triggerId: null,
  });

  const location = useLocation();
  const { fingerprint, sessionInfo, profile } = useCustomerIntelligence();

  const pageEntryTimeRef = useRef(Date.now());
  const visitedPagesRef = useRef<string[]>([]);
  const dismissalCountRef = useRef(0);

  // Track page changes
  useEffect(() => {
    pageEntryTimeRef.current = Date.now();
    visitedPagesRef.current = [...visitedPagesRef.current, location.pathname];
  }, [location.pathname]);

  // Determine page type
  const getPageType = useCallback((): CustomerContext['currentPageType'] => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path.startsWith('/producto/')) return 'product';
    if (path === '/carrito') return 'cart';
    if (path === '/checkout') return 'checkout';
    if (path.startsWith('/buscar')) return 'search';
    if (path.startsWith('/perros') || path.startsWith('/gatos') || path.startsWith('/farmacia')) {
      return 'category';
    }
    return 'general';
  }, [location.pathname]);

  // Get cooldowns from storage
  const getCooldowns = useCallback((): Record<string, number> => {
    try {
      const stored = localStorage.getItem(COOLDOWN_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }, []);

  // Set cooldown
  const setCooldown = useCallback((triggerId: string, until: number) => {
    try {
      const cooldowns = getCooldowns();
      cooldowns[triggerId] = until;
      localStorage.setItem(COOLDOWN_KEY, JSON.stringify(cooldowns));
    } catch {
      // Ignore
    }
  }, [getCooldowns]);

  // Get dismissals
  const getDismissals = useCallback((): Record<string, number> => {
    try {
      const stored = localStorage.getItem(DISMISSAL_KEY);
      if (!stored) return {};
      const data = JSON.parse(stored);
      if (data.date !== new Date().toDateString()) return {};
      return data.counts ?? {};
    } catch {
      return {};
    }
  }, []);

  // Save dismissal
  const recordDismissal = useCallback((triggerId: string) => {
    try {
      const dismissals = getDismissals();
      dismissals[triggerId] = (dismissals[triggerId] ?? 0) + 1;
      localStorage.setItem(DISMISSAL_KEY, JSON.stringify({
        date: new Date().toDateString(),
        counts: dismissals,
      }));
      dismissalCountRef.current += 1;

      // Extended cooldown after dismissal
      const trigger = TRIGGERS.find(t => t.id === triggerId);
      if (trigger) {
        const cooldownMs = trigger.cooldownMinutes * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND * EXTENDED_COOLDOWN_MULTIPLIER;
        setCooldown(triggerId, Date.now() + cooldownMs);
      }
    } catch {
      // Ignore
    }
  }, [getDismissals, setCooldown]);

  // Get session proactive count
  const getSessionCount = useCallback((): number => {
    try {
      return parseInt(sessionStorage.getItem(SESSION_COUNT_KEY) ?? '0', DEFAULT_RADIX);
    } catch {
      return 0;
    }
  }, []);

  // Increment session count
  const incrementSessionCount = useCallback(() => {
    try {
      sessionStorage.setItem(SESSION_COUNT_KEY, (getSessionCount() + 1).toString());
    } catch {
      // Ignore
    }
  }, [getSessionCount]);

  // Evaluate triggers
  const evaluate = useCallback(() => {
    // Check session limit
    if (getSessionCount() >= MAX_PROACTIVE_PER_SESSION) {
      return { shouldShow: false, message: null, triggerId: null };
    }

    // Check daily dismissal limit
    if (dismissalCountRef.current >= MAX_DAILY_DISMISSALS) {
      return { shouldShow: false, message: null, triggerId: null };
    }

    const now = Date.now();
    const cooldowns = getCooldowns();
    const dismissals = getDismissals();

    // Build context
    const context: CustomerContext = {
      fingerprintHash: fingerprint ?? '',
      customerId: profile?.customerId,
      isRecognized: (sessionInfo?.sessionCount ?? 0) > 1,
      sessionDuration: sessionInfo?.sessionDuration ?? 0,
      timeOnCurrentPage: Math.floor((now - pageEntryTimeRef.current) / MILLISECONDS_PER_SECOND),
      currentPageType: getPageType(),
      currentProductId: location.pathname.startsWith('/producto/')
        ? location.pathname.split('/producto/')[1]
        : undefined,
      pagesVisitedThisSession: visitedPagesRef.current,
      searchQueries: [],
      sessionCount: sessionInfo?.sessionCount ?? DEFAULT_SESSION_COUNT,
      hasAbandonedCart: false,
      petNames: [],
      chatMessagesThisSession: chatMessagesCount,
      proactiveMessagesDismissedToday: dismissalCountRef.current,
    };

    // Find eligible trigger
    const eligibleTriggers = TRIGGERS
      .filter(trigger => {
        if (!trigger.condition(context)) return false;
        const cooldownUntil = cooldowns[trigger.id];
        if (cooldownUntil && now < cooldownUntil) return false;
        const dismissalCount = dismissals[trigger.id] ?? 0;
        if (dismissalCount >= trigger.maxDismissalsBeforeDisable) return false;
        return true;
      })
      .sort((a, b) => b.priority - a.priority);

    if (eligibleTriggers.length === 0) {
      return { shouldShow: false, message: null, triggerId: null };
    }

    const selectedTrigger = eligibleTriggers[0];
    const message = selectedTrigger.getMessage(context);

    // Set cooldown
    const cooldownMs = selectedTrigger.cooldownMinutes * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;
    setCooldown(selectedTrigger.id, now + cooldownMs);
    incrementSessionCount();

    return {
      shouldShow: true,
      message,
      triggerId: selectedTrigger.id,
    };
  }, [
    fingerprint,
    profile,
    sessionInfo,
    location.pathname,
    chatMessagesCount,
    getPageType,
    getCooldowns,
    getDismissals,
    getSessionCount,
    setCooldown,
    incrementSessionCount,
  ]);

  // Periodic evaluation
  useEffect(() => {
    // Initial evaluation after delay
    const initialTimer = setTimeout(() => {
      setResult(evaluate());
    }, INITIAL_EVALUATION_DELAY_MS);

    // Re-evaluate periodically
    const intervalId = setInterval(() => {
      if (!result.shouldShow) {
        setResult(evaluate());
      }
    }, PERIODIC_EVALUATION_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalId);
    };
  }, [evaluate, result.shouldShow]);

  // Re-evaluate on page change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!result.shouldShow) {
        setResult(evaluate());
      }
    }, PAGE_CHANGE_EVALUATION_DELAY_MS);

    return () => clearTimeout(timer);
  }, [location.pathname, evaluate, result.shouldShow]);

  // Dismiss handler
  const dismiss = useCallback((triggerId: string) => {
    recordDismissal(triggerId);
    setResult({ shouldShow: false, message: null, triggerId: null });
  }, [recordDismissal]);

  // Engage handler (user clicked to chat)
  const engage = useCallback(() => {
    setResult({ shouldShow: false, message: null, triggerId: null });
  }, []);

  return {
    ...result,
    dismiss,
    engage,
  };
}
